import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  RiAddLine, RiEditLine, RiDeleteBinLine, RiSearchLine, RiCloseLine,
  RiCheckLine, RiUser3Line, RiBookOpenLine, RiFileTextLine, RiBarChartLine,
  RiArrowUpLine, RiProhibitedLine, RiSendPlane2Line, RiDraftLine,
  RiNotification3Line, RiQuillPenLine, RiMailLine, RiFireLine, RiCalendarLine,
} from 'react-icons/ri';
import { C, LEVELS, WORD_TYPES } from '../../data/constants';
import {
  StatCard, LevelBadge, StatusBadge, TypeBadge,
  ConfirmModal, PageHeader, FilterRow, FormGroup, EmptyState, Toast,
  LoadingScreen,
} from '../../components/shared/UI';
import {
  getAdminStats,
  getAdminAnalytics,
  adminGetVocab, adminCreateVocab, adminUpdateVocab, adminDeleteVocab,
  adminGetKanji, adminCreateKanji, adminUpdateKanji, adminDeleteKanji,
  adminGetQuiz, adminCreateQuiz, adminUpdateQuiz, adminDeleteQuiz,
  adminGetUsers, adminUpdateUserStatus, adminDeleteUser,
  adminGetAnnouncements, adminCreateAnnouncement,
  adminUpdateAnnouncement, adminDeleteAnnouncement,
} from '../../api/admin.api';

/* ── Shared helpers ──────────────────────────────────────────── */
function useToast() {
  const [msg, setMsg] = useState('');

  const show = useCallback((m) => {
    setMsg(m);
    setTimeout(() => setMsg(''), 2500);
  }, []);

  return [msg, show];
}

function Spinner({ label }) {
  return <LoadingScreen label={label} />;
}

function ErrorMsg({ message, onRetry }) {
  return (
    <div style={{ padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 13, color: C.red, marginBottom: 16 }}>{message}</div>
      {onRetry && <button className="btn-ghost" onClick={onRetry}>Try Again</button>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   OVERVIEW
══════════════════════════════════════════════════════════════ */
const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SIGNUPS = [4, 7, 3, 9, 6, 12, 8];
const ACTIVITY = [42, 38, 55, 31, 60, 48, 52];

export function Overview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getAdminStats()
      .then(res => { setStats(res.data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  if (loading) return <Spinner />;
  if (error) return <ErrorMsg message={error} onRetry={() => window.location.reload()} />;

  const maxS = Math.max(...SIGNUPS), maxA = Math.max(...ACTIVITY);

  return (
    <div style={{ padding: 32 }} className="anim-fade-up">
      <div style={{ marginBottom: 26 }}>
        <h1 style={{ fontSize: 24, fontWeight: 900, letterSpacing: -0.7, marginBottom: 4 }}>Overview</h1>
        <p style={{ color: C.muted, fontSize: 14 }}>Welcome back, Admin. Here's what's happening.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12, marginBottom: 24 }}>
        <StatCard icon={RiUser3Line} label="Total Users" value={stats.totalUsers} sub={`${stats.activeUsers} active`} color={C.blue} />
        <StatCard icon={RiBookOpenLine} label="Vocab Words" value={stats.totalVocab} sub={`+${stats.newUsers} new users`} color={C.accent} />
        <StatCard icon={RiQuillPenLine} label="Kanji" value={stats.totalKanji} sub="in database" color={C.yellow} />
        <StatCard icon={RiFileTextLine} label="Quiz Questions" value={stats.totalQuiz} sub="in database" color={C.green} />
      </div>

      <div className="ks-grid-2" style={{ gap: 16, marginBottom: 18 }}>
        {[
          { label: 'Daily Signups', data: SIGNUPS, max: maxS, color: C.accent },
          { label: 'Daily Active Users', data: ACTIVITY, max: maxA, color: C.blue },
        ].map(ch => (
          <div key={ch.label} className="ks-card" style={{ padding: 22 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{ch.label}</div>
                <div style={{ fontSize: 12, color: C.muted }}>Last 7 days</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: C.green, fontWeight: 700 }}>
                <RiArrowUpLine size={13} />+14%
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 80 }}>
              {WEEK.map((d, i) => (
                <div key={d} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: '100%', height: `${(ch.data[i] / ch.max) * 64}px`, background: i === 6 ? ch.color : `${ch.color}38`, borderRadius: '4px 4px 0 0', minHeight: 3 }} />
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>{d}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="ks-grid-2" style={{ gap: 16 }}>
        <div className="ks-card" style={{ padding: 22 }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 14 }}>Database Summary</div>
          <table className="ks-table">
            <thead><tr><th>Collection</th><th>Total</th><th>Status</th></tr></thead>
            <tbody>
              {[
                { name: 'Vocabulary', count: stats.totalVocab },
                { name: 'Kanji', count: stats.totalKanji },
                { name: 'Quiz Qs', count: stats.totalQuiz },
                { name: 'Users', count: stats.totalUsers },
              ].map(r => (
                <tr key={r.name}>
                  <td style={{ fontWeight: 600 }}>{r.name}</td>
                  <td style={{ fontWeight: 700 }}>{r.count}</td>
                  <td>
                    <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '3px 8px', background: 'rgba(34,197,94,0.12)', color: C.green }}>
                      Live
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ks-card" style={{ padding: 22 }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 14 }}>Quick Stats</div>
          {[
            { label: 'New users this week', value: stats.newUsers, color: C.green },
            { label: 'Active users', value: stats.activeUsers, color: C.blue },
            { label: 'Banned users', value: stats.bannedUsers, color: C.red },
            { label: 'Total content items', value: stats.totalVocab + stats.totalKanji + stats.totalQuiz, color: C.accent },
          ].map((s, i, arr) => (
            <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <span style={{ fontSize: 13, color: C.muted }}>{s.label}</span>
              <span style={{ fontSize: 16, fontWeight: 900, color: s.color }}>{s.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   VOCABULARY MANAGER
══════════════════════════════════════════════════════════════ */
const EMPTY_VOCAB = { level: 'N5', kanji: '', kana: '', romaji: '', meaning: '', type: 'noun', example: '', exampleEn: '' };

export function VocabularyManager() {
  const [words, setWords] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [fLevel, setFLevel] = useState('All');
  const [fType, setFType] = useState('All');
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY_VOCAB);
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, showToast] = useToast();

  // const load = async () => {
  //   setLoading(true);
  //   try {
  //     const params = { limit: 100 };
  //     if (fLevel !== 'All') params.level = fLevel;
  //     if (fType !== 'All') params.type = fType;
  //     if (search) params.search = search;
  //     const res = await adminGetVocab(params);
  //     setWords(res.data);
  //     setTotal(res.total);
  //   } catch (err) { setError(err.message); }
  //   finally { setLoading(false); }
  // };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (fLevel !== 'All') params.level = fLevel;
      if (fType !== 'All') params.type = fType;
      if (search) params.search = search;
      const res = await adminGetVocab(params);
      setWords(res.data);
      setTotal(res.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, fLevel, fType]);

  useEffect(() => {
    load();
  }, [load]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [search, fLevel, fType]);

  const openAdd = () => { setForm(EMPTY_VOCAB); setEditId(null); setModal(true); };
  const openEdit = w => { setForm({ level: w.level, kanji: w.kanji, kana: w.kana, romaji: w.romaji || '', meaning: w.meaning, type: w.type, example: w.example || '', exampleEn: w.exampleEn || '' }); setEditId(w._id); setModal(true); };
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.kanji || !form.kana || !form.meaning) return;
    setSaving(true);
    try {
      if (editId) {
        const res = await adminUpdateVocab(editId, form);
        setWords(ws => ws.map(w => w._id === editId ? res.data : w));
        showToast('Word updated!');
      } else {
        const res = await adminCreateVocab(form);
        setWords(ws => [res.data, ...ws]);
        setTotal(t => t + 1);
        showToast('Word added!');
      }
      setModal(false);
    } catch (err) { showToast(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await adminDeleteVocab(delId);
      setWords(ws => ws.filter(w => w._id !== delId));
      setTotal(t => t - 1);
      showToast('Word deleted.');
    } catch (err) { showToast(err.message); }
    finally { setDelId(null); }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorMsg message={error} onRetry={load} />;

  return (
    <div style={{ padding: 32 }} className="anim-fade-up">
      <PageHeader title="Vocabulary" sub={`${total} total words`} action={<button className="btn-primary" onClick={openAdd}><RiAddLine />Add Word</button>} />

      <FilterRow>
        <div className="search-wrap" style={{ flex: 1, minWidth: 200 }}>
          <RiSearchLine size={15} />
          <input className="ks-input" placeholder="Search kanji, kana, meaning…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="ks-select" style={{ width: 110 }} value={fLevel} onChange={e => setFLevel(e.target.value)}>
          <option>All</option>{LEVELS.map(l => <option key={l}>{l}</option>)}
        </select>
        <select className="ks-select" style={{ width: 120 }} value={fType} onChange={e => setFType(e.target.value)}>
          <option>All</option>{WORD_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <span style={{ fontSize: 13, color: C.muted }}>{words.length} results</span>
      </FilterRow>

      <div className="ks-card" style={{ overflow: 'hidden' }}>
        {words.length === 0
          ? <EmptyState icon={RiSearchLine} title="No words found" sub="Try a different search or add a new word." />
          : (
            <div style={{ overflowX: 'auto' }}>
              <table className="ks-table">
                <thead>
                  <tr><th>Kanji</th><th>Kana</th><th>Romaji</th><th>Meaning</th><th>Type</th><th>Level</th><th>Example</th><th>Added</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {words.map(w => (
                    <tr key={w._id}>
                      <td style={{ fontFamily: "'Noto Sans JP',sans-serif", fontSize: 20, fontWeight: 900 }}>{w.kanji}</td>
                      <td style={{ fontFamily: "'Noto Sans JP',sans-serif", fontSize: 13, color: C.muted }}>{w.kana}</td>
                      <td style={{ fontSize: 13, color: C.muted }}>{w.romaji || '—'}</td>
                      <td style={{ fontWeight: 600 }}>{w.meaning}</td>
                      <td><TypeBadge type={w.type} /></td>
                      <td><LevelBadge level={w.level} /></td>
                      <td style={{ fontSize: 12, color: C.muted, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.example}</td>
                      <td style={{ fontSize: 11, color: C.faint }}>{new Date(w.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-icon" onClick={() => openEdit(w)}><RiEditLine size={14} /></button>
                          <button className="btn-icon danger" onClick={() => setDelId(w._id)}><RiDeleteBinLine size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </div>

      {modal && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflowY: 'auto' }}
          onClick={() => setModal(false)}
        >
          <div
            style={{ width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', borderRadius: 12, border: `1px solid ${C.border}`, background: C.card, padding: 28, margin: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
              <h2 style={{ fontSize: 18, fontWeight: 900 }}>{editId ? 'Edit Word' : 'Add Word'}</h2>
              <button className="btn-icon" onClick={() => setModal(false)}><RiCloseLine size={16} /></button>
            </div>
            <div className="ks-grid-2" style={{ gap: 12 }}>
              <FormGroup label="Level"><select className="ks-select" value={form.level} onChange={e => f('level', e.target.value)}>{LEVELS.map(l => <option key={l}>{l}</option>)}</select></FormGroup>
              <FormGroup label="Type"><select className="ks-select" value={form.type} onChange={e => f('type', e.target.value)}>{WORD_TYPES.map(t => <option key={t}>{t}</option>)}</select></FormGroup>
            </div>
            <div className="ks-grid-2" style={{ gap: 12 }}>
              <FormGroup label="Kanji *"><input className="ks-input" placeholder="e.g. 水" value={form.kanji} onChange={e => f('kanji', e.target.value)} style={{ fontFamily: "'Noto Sans JP',sans-serif", fontSize: 18 }} /></FormGroup>
              <FormGroup label="Kana *"><input className="ks-input" placeholder="e.g. みず" value={form.kana} onChange={e => f('kana', e.target.value)} style={{ fontFamily: "'Noto Sans JP',sans-serif" }} /></FormGroup>
            </div>
            <FormGroup label="Romaji"><input className="ks-input" placeholder="e.g. Mizu" value={form.romaji} onChange={e => f('romaji', e.target.value)} /></FormGroup>
            <FormGroup label="Meaning *"><input className="ks-input" placeholder="e.g. Water" value={form.meaning} onChange={e => f('meaning', e.target.value)} /></FormGroup>
            <div className="ks-grid-2" style={{ gap: 12 }}>
              <FormGroup label="Example (JP)"><input className="ks-input" placeholder="e.g. 水を飲む" value={form.example} onChange={e => f('example', e.target.value)} style={{ fontFamily: "'Noto Sans JP',sans-serif" }} /></FormGroup>
              <FormGroup label="Example (EN)"><input className="ks-input" placeholder="e.g. Drink water" value={form.exampleEn} onChange={e => f('exampleEn', e.target.value)} /></FormGroup>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setModal(false)}>Cancel</button>
              <button className="btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={save} disabled={saving}>
                {saving ? <div className="spinner" /> : <><RiCheckLine />{editId ? 'Save' : 'Add Word'}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {delId && <ConfirmModal message="This word will be permanently deleted from the database." onConfirm={handleDelete} onCancel={() => setDelId(null)} />}
      <Toast message={toast} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   KANJI MANAGER
══════════════════════════════════════════════════════════════ */
const EMPTY_KANJI = {
  level: 'N5',
  char: '',
  meaning: '',
  names: '',
  kunyomi: '',
  kunyomiEnglish: '',
  onyomi: '',
  onyomiEnglish: '',
  popularReading: '',
  strokes: '',
  examples: '',
};

const csv = value => String(value || '').split(/[、,]/).map(v => v.trim()).filter(Boolean);
const capFirst = value => {
  const text = String(value || '').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
};
const capCsv = value => csv(value).map(capFirst).join(', ');
const combinedReadings = form => [...csv(form.onyomi), ...csv(form.kunyomi)].join(', ');
const splitLegacyReadings = value => csv(value).reduce((acc, reading) => {
  if (/[\u30a0-\u30ff]/.test(reading)) acc.onyomi.push(reading);
  else acc.kunyomi.push(reading);
  return acc;
}, { onyomi: [], kunyomi: [] });
const Chip = ({ children, color = C.accent }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 8px', borderRadius: 6, background: `${color}14`, border: `1px solid ${color}32`, color, fontSize: 11, fontWeight: 800, lineHeight: 1.2 }}>
    {children}
  </span>
);

export function KanjiManager() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [fLevel, setFLevel] = useState('All');
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY_KANJI);
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, showToast] = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (fLevel !== 'All') params.level = fLevel;
      if (search) params.search = search;
      const res = await adminGetKanji(params);
      setList(res.data);
    } catch (err) { showToast(err.message); }
    finally {
      setLoading(false);
    }
  }, [search, fLevel, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [search, fLevel]);

  const openEdit = k => {
    const legacy = splitLegacyReadings(k.readings);
    setForm({
      level: k.level,
      char: k.char,
      meaning: k.meaning,
      names: k.names || k.meaning || '',
      kunyomi: k.kunyomi || legacy.kunyomi.join(', '),
      kunyomiEnglish: k.kunyomiEnglish || '',
      onyomi: k.onyomi || legacy.onyomi.join(', '),
      onyomiEnglish: k.onyomiEnglish || '',
      popularReading: k.popularReading || '',
      strokes: String(k.strokes),
      examples: k.examples || '',
    });
    setEditId(k._id);
    setModal(true);
  };
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.char || !form.meaning) return;
    setSaving(true);
    try {
      const body = {
        ...form,
        meaning: capFirst(form.meaning),
        names: capCsv(form.names || form.meaning),
        onyomiEnglish: capCsv(form.onyomiEnglish),
        kunyomiEnglish: capCsv(form.kunyomiEnglish),
        readings: combinedReadings(form),
        strokes: parseInt(form.strokes) || 0,
      };
      if (editId) {
        const res = await adminUpdateKanji(editId, body);
        setList(l => l.map(k => k._id === editId ? res.data : k));
        showToast('Updated!');
      } else {
        const res = await adminCreateKanji(body);
        setList(l => [res.data, ...l]);
        showToast('Added!');
      }
      setModal(false);
    } catch (err) { showToast(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await adminDeleteKanji(delId); setList(l => l.filter(k => k._id !== delId)); showToast('Deleted.'); }
    catch (err) { showToast(err.message); }
    finally { setDelId(null); }
  };

  useEffect(() => {
    if (!modal) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [modal]);

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: 32 }} className="anim-fade-up">
      <PageHeader title="Kanji" sub={`${list.length} total`} action={<button className="btn-primary" onClick={() => { setForm(EMPTY_KANJI); setEditId(null); setModal(true); }}><RiAddLine />Add Kanji</button>} />

      <FilterRow>
        <div className="search-wrap" style={{ flex: 1, minWidth: 200 }}><RiSearchLine size={15} /><input className="ks-input" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} /></div>
        <select className="ks-select" style={{ width: 110 }} value={fLevel} onChange={e => setFLevel(e.target.value)}><option>All</option>{LEVELS.map(l => <option key={l}>{l}</option>)}</select>
        <span style={{ fontSize: 13, color: C.muted }}>{list.length} results</span>
      </FilterRow>

      <div className="ks-card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="ks-table">
            <thead><tr><th>Char</th><th>Display Name</th><th>All Names</th><th>Popular</th><th>Onyomi</th><th>Kunyomi</th><th>Strokes</th><th>Level</th><th>Actions</th></tr></thead>
            <tbody>
              {list.map(k => (
                <tr key={k._id}>
                  <td style={{ fontFamily: "'Noto Sans JP',sans-serif", fontSize: 32, fontWeight: 900, lineHeight: 1 }}>{k.char}</td>
                  <td style={{ fontWeight: 600 }}>{capFirst(k.meaning)}</td>
                  <td style={{ fontSize: 12, color: C.muted, maxWidth: 170 }}>{capCsv(k.names || k.meaning)}</td>
                  <td>{k.popularReading ? <Chip color={C.green}>{k.popularReading}</Chip> : <span style={{ color: C.faint }}>-</span>}</td>
                  <td style={{ fontFamily: "'Noto Sans JP',sans-serif", fontSize: 12, color: C.muted, maxWidth: 150 }}>{k.onyomi || (!k.kunyomi ? k.readings : '')}</td>
                  <td style={{ fontFamily: "'Noto Sans JP',sans-serif", fontSize: 12, color: C.muted, maxWidth: 150 }}>{k.kunyomi}</td>
                  <td style={{ fontWeight: 700, textAlign: 'center' }}>{k.strokes}</td>
                  <td><LevelBadge level={k.level} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-icon" onClick={() => openEdit(k)}><RiEditLine size={14} /></button>
                      <button className="btn-icon danger" onClick={() => setDelId(k._id)}><RiDeleteBinLine size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && createPortal((
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div
            className="modal-box"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 900, maxHeight: 'calc(100vh - 48px)', overflowY: 'auto', padding: 24 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 style={{ fontSize: 18, fontWeight: 900 }}>{editId ? 'Edit Kanji' : 'Add Kanji'}</h2>

              <button className="btn-icon" onClick={() => setModal(false)}><RiCloseLine size={16} /></button>
            </div>
            <div className="ks-grid-3" style={{ gap: 10 }}>
              <FormGroup label="Level"><select className="ks-select" value={form.level} onChange={e => f('level', e.target.value)}>{LEVELS.map(l => <option key={l}>{l}</option>)}</select></FormGroup>
              <FormGroup label="Strokes"><input className="ks-input" type="number" placeholder="e.g. 4" value={form.strokes} onChange={e => f('strokes', e.target.value)} /></FormGroup>
              <FormGroup label="Popular Reading"><input className="ks-input" placeholder="e.g. みず" value={form.popularReading} onChange={e => f('popularReading', e.target.value)} style={{ fontFamily: "'Noto Sans JP',sans-serif", borderColor: form.popularReading ? C.green : undefined }} /></FormGroup>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '88px 1fr 1fr', gap: 10 }}>
              <FormGroup label="Char *"><input className="ks-input" placeholder="水" value={form.char} onChange={e => f('char', e.target.value)} style={{ fontFamily: "'Noto Sans JP',sans-serif", fontSize: 26, textAlign: 'center', padding: 8 }} /></FormGroup>
              <FormGroup label="Display Name *"><input className="ks-input" placeholder="e.g. Water" value={form.meaning} onChange={e => f('meaning', e.target.value)} /></FormGroup>
              <FormGroup label="All Names"><input className="ks-input" placeholder="Water, Wednesday, fluid" value={form.names} onChange={e => f('names', e.target.value)} /></FormGroup>
            </div>
            <div className="ks-grid-2" style={{ gap: 10 }}>
              <FormGroup label="Onyomi"><input className="ks-input" placeholder="スイ" value={form.onyomi} onChange={e => f('onyomi', e.target.value)} style={{ fontFamily: "'Noto Sans JP',sans-serif" }} /></FormGroup>
              <FormGroup label="Onyomi English"><input className="ks-input" placeholder="sui" value={form.onyomiEnglish} onChange={e => f('onyomiEnglish', e.target.value)} /></FormGroup>
              <FormGroup label="Kunyomi"><input className="ks-input" placeholder="みず" value={form.kunyomi} onChange={e => f('kunyomi', e.target.value)} style={{ fontFamily: "'Noto Sans JP',sans-serif" }} /></FormGroup>
              <FormGroup label="Kunyomi English"><input className="ks-input" placeholder="mizu" value={form.kunyomiEnglish} onChange={e => f('kunyomiEnglish', e.target.value)} /></FormGroup>
            </div>
            <FormGroup label="Examples"><textarea className="ks-textarea" placeholder="e.g. 水曜日 (Wednesday), 水泳 (Swimming)" value={form.examples} onChange={e => f('examples', e.target.value)} style={{ fontFamily: "'Noto Sans JP',sans-serif", minHeight: 58 }} /></FormGroup>
            {(form.names || form.onyomi || form.kunyomi || form.popularReading) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 10px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 10 }}>
                {form.popularReading && <Chip color={C.green}>popular: {form.popularReading}</Chip>}
                {csv(form.names).map(n => <Chip key={`n-${n}`}>{n}</Chip>)}
                {csv(form.onyomi).map(r => <Chip key={`o-${r}`}>on: {r}</Chip>)}
                {csv(form.kunyomi).map(r => <Chip key={`k-${r}`}>kun: {r}</Chip>)}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              <button className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setModal(false)}>Cancel</button>
              <button className="btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={save} disabled={saving}>
                {saving ? <div className="spinner" /> : <><RiCheckLine />{editId ? 'Save' : 'Add'}</>}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {delId && <ConfirmModal message="This kanji will be permanently deleted." onConfirm={handleDelete} onCancel={() => setDelId(null)} />}
      <Toast message={toast} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   QUIZ MANAGER
══════════════════════════════════════════════════════════════ */
const EMPTY_QUIZ = { level: 'N5', question: '', opt0: '', opt1: '', opt2: '', opt3: '', answer: 0 };
const OPT_LABELS = ['A', 'B', 'C', 'D'];

export function QuizManager() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [fLevel, setFLevel] = useState('All');
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY_QUIZ);
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, showToast] = useToast();

  const toForm = q => ({ level: q.level, question: q.question, opt0: q.options[0] || '', opt1: q.options[1] || '', opt2: q.options[2] || '', opt3: q.options[3] || '', answer: q.answer });

  // const load = async () => {
  //   setLoading(true);
  //   try {
  //     const params = { limit: 100 };
  //     if (fLevel !== 'All') params.level = fLevel;
  //     const res = await adminGetQuiz(params);
  //     setList(res.data);
  //   } catch (err) { showToast(err.message); }
  //   finally { setLoading(false); }
  // };
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (fLevel !== 'All') params.level = fLevel;
      const res = await adminGetQuiz(params);
      setList(res.data);
    } catch (err) {
      showToast(err.message);
    } finally {
      setLoading(false);
    }
  }, [fLevel, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [fLevel]);

  const filtered = search ? list.filter(q => q.question.toLowerCase().includes(search.toLowerCase())) : list;
  const openEdit = q => { setForm(toForm(q)); setEditId(q._id); setModal(true); };
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.question || !form.opt0 || !form.opt1) return;
    setSaving(true);
    const body = { level: form.level, question: form.question, options: [form.opt0, form.opt1, form.opt2, form.opt3], answer: parseInt(form.answer) };
    try {
      if (editId) {
        const res = await adminUpdateQuiz(editId, body);
        setList(l => l.map(q => q._id === editId ? res.data : q));
        showToast('Updated!');
      } else {
        const res = await adminCreateQuiz(body);
        setList(l => [res.data, ...l]);
        showToast('Added!');
      }
      setModal(false);
    } catch (err) { showToast(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await adminDeleteQuiz(delId); setList(l => l.filter(q => q._id !== delId)); showToast('Deleted.'); }
    catch (err) { showToast(err.message); }
    finally { setDelId(null); }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: 32 }} className="anim-fade-up">
      <PageHeader title="Quiz Questions" sub={`${list.length} questions`} action={<button className="btn-primary" onClick={() => { setForm(EMPTY_QUIZ); setEditId(null); setModal(true); }}><RiAddLine />Add Question</button>} />

      <FilterRow>
        <div className="search-wrap" style={{ flex: 1, minWidth: 200 }}><RiSearchLine size={15} /><input className="ks-input" placeholder="Search questions…" value={search} onChange={e => setSearch(e.target.value)} /></div>
        <select className="ks-select" style={{ width: 110 }} value={fLevel} onChange={e => setFLevel(e.target.value)}><option>All</option>{LEVELS.map(l => <option key={l}>{l}</option>)}</select>
        <span style={{ fontSize: 13, color: C.muted }}>{filtered.length} results</span>
      </FilterRow>

      <div className="ks-card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="ks-table">
            <thead><tr><th>Level</th><th>Question</th><th>A</th><th>B</th><th>C</th><th>D</th><th>Answer</th><th>Added</th><th>Actions</th></tr></thead>
            <tbody>
              {filtered.map(q => (
                <tr key={q._id}>
                  <td><LevelBadge level={q.level} /></td>
                  <td style={{ fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.question}</td>
                  {q.options.map((o, i) => (
                    <td key={i} style={{ fontSize: 13, color: i === q.answer ? C.green : C.muted, fontWeight: i === q.answer ? 700 : 400, fontFamily: "'Noto Sans JP',sans-serif" }}>{o}</td>
                  ))}
                  <td>
                    <span style={{ background: C.accentDim, border: `1px solid ${C.accentBorder}`, color: C.accent, borderRadius: 6, padding: '3px 9px', fontSize: 12, fontWeight: 700 }}>
                      {OPT_LABELS[q.answer]}
                    </span>
                  </td>
                  <td style={{ fontSize: 11, color: C.faint }}>{new Date(q.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-icon" onClick={() => openEdit(q)}><RiEditLine size={14} /></button>
                      <button className="btn-icon danger" onClick={() => setDelId(q._id)}><RiDeleteBinLine size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 900 }}>{editId ? 'Edit Question' : 'Add Question'}</h2>
              <button className="btn-icon" onClick={() => setModal(false)}><RiCloseLine size={16} /></button>
            </div>
            <FormGroup label="Level"><select className="ks-select" value={form.level} onChange={e => f('level', e.target.value)}>{LEVELS.map(l => <option key={l}>{l}</option>)}</select></FormGroup>
            <FormGroup label="Question *"><input className="ks-input" placeholder="e.g. What does 水 mean?" value={form.question} onChange={e => f('question', e.target.value)} /></FormGroup>
            <div className="ks-grid-2" style={{ gap: 12 }}>
              {['opt0', 'opt1', 'opt2', 'opt3'].map((key, i) => (
                <FormGroup key={key} label={`Option ${OPT_LABELS[i]}${parseInt(form.answer) === i ? ' ✓' : ''}`}>
                  <input className="ks-input" placeholder={`Option ${OPT_LABELS[i]}`} value={form[key]} onChange={e => f(key, e.target.value)} style={{ borderColor: parseInt(form.answer) === i ? C.green : undefined }} />
                </FormGroup>
              ))}
            </div>
            <FormGroup label="Correct Answer">
              <select className="ks-select" value={form.answer} onChange={e => f('answer', e.target.value)}>
                {OPT_LABELS.map((l, i) => <option key={i} value={i}>Option {l}</option>)}
              </select>
            </FormGroup>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setModal(false)}>Cancel</button>
              <button className="btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={save} disabled={saving}>
                {saving ? <div className="spinner" /> : <><RiCheckLine />{editId ? 'Save' : 'Add'}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {delId && <ConfirmModal message="This question will be permanently deleted." onConfirm={handleDelete} onCancel={() => setDelId(null)} />}
      <Toast message={toast} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   USERS MANAGER
══════════════════════════════════════════════════════════════ */
export function UsersManager() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [fStatus, setFStatus] = useState('All');
  const [fLevel, setFLevel] = useState('All');
  const [delId, setDelId] = useState(null);
  const [banId, setBanId] = useState(null);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, showToast] = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (fStatus !== 'All') params.status = fStatus;
      if (fLevel !== 'All') params.level = fLevel;
      if (search) params.search = search;
      const res = await adminGetUsers(params);
      setUsers(res.data);
      setTotal(res.total);
    } catch (err) {
      showToast(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, fStatus, fLevel, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [search, fStatus, fLevel]);

  const toggleBan = async () => {
    const u = users.find(x => x._id === banId);
    const newStatus = u?.status === 'banned' ? 'active' : 'banned';
    try {
      const res = await adminUpdateUserStatus(banId, newStatus);
      setUsers(us => us.map(x => x._id === banId ? res.data : x));
      if (sel?._id === banId) setSel(res.data);
      showToast(newStatus === 'banned' ? 'User banned.' : 'User unbanned.');
    } catch (err) { showToast(err.message); }
    finally { setBanId(null); }
  };

  const handleDelete = async () => {
    try {
      await adminDeleteUser(delId);
      setUsers(us => us.filter(u => u._id !== delId));
      setTotal(t => t - 1);
      if (sel?._id === delId) setSel(null);
      showToast('User deleted.');
    } catch (err) { showToast(err.message); }
    finally { setDelId(null); }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: 32 }} className="anim-fade-up">
      <PageHeader title="Users" sub={`${total} total · ${users.filter(u => u.status === 'active').length} active · ${users.filter(u => u.status === 'banned').length} banned`} />

      <FilterRow>
        <div className="search-wrap" style={{ flex: 1, minWidth: 200 }}><RiSearchLine size={15} /><input className="ks-input" placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)} /></div>
        <select className="ks-select" style={{ width: 120 }} value={fStatus} onChange={e => setFStatus(e.target.value)}>
          <option>All</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="banned">Banned</option>
        </select>
        <select className="ks-select" style={{ width: 110 }} value={fLevel} onChange={e => setFLevel(e.target.value)}><option>All</option>{LEVELS.map(l => <option key={l}>{l}</option>)}</select>
        <span style={{ fontSize: 13, color: C.muted }}>{users.length} results</span>
      </FilterRow>

      <div style={{ display: 'grid', gridTemplateColumns: sel ? '1fr 320px' : '1fr', gap: 16 }}>
        <div className="ks-card" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="ks-table">
              <thead><tr><th>User</th><th>Level</th><th>XP</th><th>Streak</th><th>Status</th><th>Joined</th><th>Actions</th></tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id} style={{ cursor: 'pointer' }} onClick={() => setSel(sel?._id === u._id ? null : u)}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: `${C.accent}20`, color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12, flexShrink: 0 }}>{u.name.charAt(0)}</div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{u.name}</div>
                          <div style={{ fontSize: 11, color: C.muted }}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td><LevelBadge level={u.currentLevel} /></td>
                    <td style={{ fontWeight: 700 }}>{u.xp.toLocaleString()}</td>
                    <td style={{ fontWeight: 700, color: u.streak > 0 ? C.accent : C.muted }}>{u.streak}d</td>
                    <td><StatusBadge status={u.status} /></td>
                    <td style={{ fontSize: 11, color: C.faint }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-icon" onClick={() => setBanId(u._id)} style={{ color: u.status === 'banned' ? C.green : C.yellow }}>
                          {u.status === 'banned' ? <RiCheckLine size={14} /> : <RiProhibitedLine size={14} />}
                        </button>
                        <button className="btn-icon danger" onClick={() => setDelId(u._id)}><RiDeleteBinLine size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {sel && (
          <div className="ks-card anim-pop" style={{ padding: 22, alignSelf: 'start', position: 'sticky', top: 16 }}>
            <div style={{ textAlign: 'center', marginBottom: 18 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${C.accent}20`, color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 22, margin: '0 auto 10px' }}>{sel.name.charAt(0)}</div>
              <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 2 }}>{sel.name}</div>
              <div style={{ fontSize: 12, color: C.muted, display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}><RiMailLine size={11} />{sel.email}</div>
              <div style={{ marginTop: 8 }}><StatusBadge status={sel.status} /></div>
            </div>
            {[
              ['Level', sel.currentLevel],
              ['XP', sel.xp.toLocaleString()],
              ['Streak', `${sel.streak} days`],
              ['Joined', new Date(sel.createdAt).toLocaleDateString()],
            ].map(([l, v], i, arr) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                <span style={{ fontSize: 12, color: C.muted }}>{l}</span>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{v}</span>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn-danger" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setBanId(sel._id)}>
                <RiProhibitedLine size={13} />{sel.status === 'banned' ? 'Unban' : 'Ban'}
              </button>
              <button className="btn-danger" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setDelId(sel._id)}>
                <RiDeleteBinLine size={13} />Delete
              </button>
            </div>
          </div>
        )}
      </div>

      {banId && <ConfirmModal message={`Are you sure you want to ${users.find(u => u._id === banId)?.status === 'banned' ? 'unban' : 'ban'} this user?`} onConfirm={toggleBan} onCancel={() => setBanId(null)} />}
      {delId && <ConfirmModal message="This user and all their data will be permanently deleted." onConfirm={handleDelete} onCancel={() => setDelId(null)} />}
      <Toast message={toast} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ANALYTICS
══════════════════════════════════════════════════════════════ */
/* ── A bar chart that reads its own scale ─────────────────────── */
function MiniBars({ rows, valueKey, color, suffix = '', emptyNote }) {
  const values = rows.map(r => r[valueKey]).filter(v => v !== null && v !== undefined);
  const max = Math.max(1, ...values);
  const allZero = values.every(v => !v);

  if (allZero) {
    return <div style={{ padding: '26px 0', textAlign: 'center', fontSize: 12.5, color: C.faint }}>{emptyNote}</div>;
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 108 }}>
      {rows.map((r, i) => {
        const v = r[valueKey];
        const missing = v === null || v === undefined;
        const last = i === rows.length - 1;
        return (
          <div key={r.label + i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, minWidth: 0 }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, color: missing ? C.faint : color }}>
              {missing ? '—' : `${v}${suffix}`}
            </div>
            <div
              title={missing ? 'No signups that month' : `${r.label}: ${v}${suffix}`}
              style={{
                width: '100%',
                height: `${missing ? 2 : Math.max(3, (v / max) * 74)}px`,
                background: missing ? 'rgba(255,255,255,0.07)' : (last ? color : `${color}3d`),
                borderRadius: '4px 4px 0 0',
                transition: 'height 0.4s cubic-bezier(0.22,1,0.36,1)',
              }}
            />
            <div style={{ fontSize: 9.5, color: C.muted, fontWeight: 600, whiteSpace: 'nowrap' }}>{r.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function InventoryRow({ label, value, total }) {
  const pct = total ? Math.min(100, Math.round((value / total) * 100)) : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <span style={{ flex: 1, fontSize: 13, color: C.muted, fontWeight: 700 }}>{label}</span>
      {pct !== null && (
        <span style={{ width: 90, height: 5, borderRadius: 999, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
          <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: C.accent, borderRadius: 999 }} />
        </span>
      )}
      <strong style={{ fontSize: 14, fontWeight: 900, minWidth: 46, textAlign: 'right' }}>
        {total ? `${value}/${total}` : value}
      </strong>
    </div>
  );
}

export function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getAdminAnalytics()
      .then(res => setData(res.data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (error) return <div style={{ padding: 32 }}><ErrorMsg message={error} onRetry={() => window.location.reload()} /></div>;

  const h = data.headline;
  const c = data.content;
  const e = data.engagement;
  const activePct = h.totalUsers ? Math.round((h.activeThisWeek / h.totalUsers) * 100) : 0;

  return (
    <div style={{ padding: 32 }} className="anim-fade-up">
      <PageHeader
        title="Analytics"
        sub={`Live from the database · updated ${new Date(data.generatedAt).toLocaleTimeString()}`}
      />

      {/* ── People ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12, marginBottom: 14 }}>
        <StatCard icon={RiUser3Line} label="Total Learners" value={h.totalUsers} sub={`${h.newThisWeek} joined this week`} color={C.blue} />
        <StatCard icon={RiBarChartLine} label="Active This Week" value={h.activeThisWeek} sub={`${activePct}% of all learners`} color={C.accent} />
        <StatCard icon={RiFireLine} label="On a Streak" value={h.onAStreak} sub={`longest is ${h.longestStreak} days`} color={C.yellow} />
        <StatCard icon={RiCheckLine} label="Verified Emails" value={h.verifiedUsers} sub={`${h.pendingSignups} awaiting a code`} color={C.green} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12, marginBottom: 22 }}>
        <StatCard icon={RiCalendarLine} label="Study Days Logged" value={h.studyDaysLogged} sub="across all learners" color={C.purple} />
        <StatCard icon={RiFileTextLine} label="Quiz Attempts" value={e.quizAttempts} sub={`${e.avgQuizScore}% average score`} color={C.blue} />
        <StatCard icon={RiEditLine} label="Grammar Parts Cleared" value={e.grammarPartsCleared} sub={`of ${c.grammarParts} authored`} color={C.accent} />
        <StatCard icon={RiNotification3Line} label="Banned" value={h.bannedUsers} sub="accounts blocked" color={C.red} />
      </div>

      {/* ── Trends ── */}
      <div className="ks-grid-2" style={{ gap: 16, marginBottom: 16 }}>
        <div className="ks-card" style={{ padding: 22 }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 3 }}>Signups per month</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>Last 6 months, from account creation dates</div>
          <MiniBars rows={data.growth} valueKey="signups" color={C.accent} emptyNote="No signups in the last 6 months yet." />
        </div>

        <div className="ks-card" style={{ padding: 22 }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 3 }}>Cohort retention</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
            Of those who joined that month, the share who studied in the last 30 days
          </div>
          <MiniBars rows={data.growth} valueKey="retentionPct" color={C.green} suffix="%" emptyNote="No cohorts to measure yet." />
        </div>
      </div>

      <div className="ks-card" style={{ padding: 22, marginBottom: 16 }}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 3 }}>Study sessions, last 14 days</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>One count per learner per day they practised anything</div>
        <MiniBars rows={data.daily} valueKey="sessions" color={C.blue} emptyNote="Nobody has studied in the last two weeks." />
      </div>

      {/* ── What's in the app ── */}
      <div className="ks-grid-2" style={{ gap: 16 }}>
        <div className="ks-card" style={{ padding: 22 }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 12 }}>Content inventory</div>
          <InventoryRow label="Kana characters" value={c.kana} />
          <InventoryRow label="Kanji" value={c.kanji} />
          <InventoryRow label="Vocabulary words" value={c.vocabulary} />
          <InventoryRow label="Quiz questions" value={c.quizQuestions} />
          <InventoryRow label="Practice tests" value={c.practiceTests} />
        </div>

        <div className="ks-card" style={{ padding: 22 }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 12 }}>Grammar course</div>
          <InventoryRow label="Chapters published" value={c.grammarPublished} total={c.grammarChapters} />
          <InventoryRow label="Chapters with content" value={c.grammarAuthored} total={c.grammarChapters} />
          <InventoryRow label="Parts authored" value={c.grammarParts} />
          <InventoryRow label="Parts cleared by learners" value={e.grammarPartsCleared} />
          <InventoryRow label="Practice test attempts" value={e.practiceTestAttempts} />
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ANNOUNCEMENTS
══════════════════════════════════════════════════════════════ */
const EMPTY_ANN = { title: '', body: '', target: 'all', status: 'draft' };

export function AnnouncementsManager() {
  const [list, setList] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY_ANN);
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, showToast] = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminGetAnnouncements();
      setList(res.data);
    } catch (err) {
      showToast(err.message);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const openEdit = a => { setForm({ title: a.title, body: a.body, target: a.target, status: a.status }); setEditId(a._id); setModal(true); };

  const save = async (publish = false) => {
    if (!form.title || !form.body) return;
    setSaving(true);
    const body = { ...form, status: publish ? 'published' : form.status };
    try {
      if (editId) {
        const res = await adminUpdateAnnouncement(editId, body);
        setList(l => l.map(a => a._id === editId ? res.data : a));
        showToast(publish ? 'Published!' : 'Updated!');
      } else {
        const res = await adminCreateAnnouncement(body);
        setList(l => [res.data, ...l]);
        showToast(publish ? 'Published!' : 'Draft saved!');
      }
      setModal(false);
    } catch (err) { showToast(err.message); }
    finally { setSaving(false); }
  };

  const togglePublish = async a => {
    const next = a.status === 'published' ? 'draft' : 'published';
    try {
      const res = await adminUpdateAnnouncement(a._id, { ...a, status: next });
      setList(l => l.map(x => x._id === a._id ? res.data : x));
      showToast(next === 'published' ? 'Published!' : 'Moved to draft.');
    } catch (err) { showToast(err.message); }
  };

  const handleDelete = async () => {
    try { await adminDeleteAnnouncement(delId); setList(l => l.filter(a => a._id !== delId)); showToast('Deleted.'); }
    catch (err) { showToast(err.message); }
    finally { setDelId(null); }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: 32 }} className="anim-fade-up">
      <PageHeader
        title="Announcements"
        sub={`${list.filter(a => a.status === 'published').length} published · ${list.filter(a => a.status === 'draft').length} drafts`}
        action={<button className="btn-primary" onClick={() => { setForm(EMPTY_ANN); setEditId(null); setModal(true); }}><RiAddLine />New</button>}
      />

      {list.length === 0
        ? <div className="ks-card"><EmptyState icon={RiNotification3Line} title="No announcements yet" sub="Create your first announcement." /></div>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {list.map(a => (
              <div key={a._id} className="ks-card" style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: a.status === 'published' ? `${C.green}16` : C.accentDim, color: a.status === 'published' ? C.green : C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    {a.status === 'published' ? <RiSendPlane2Line /> : <RiDraftLine />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 800, fontSize: 15 }}>{a.title}</span>
                      <StatusBadge status={a.status} />
                      <span style={{ fontSize: 11, color: C.faint, background: 'rgba(255,255,255,0.05)', borderRadius: 6, padding: '2px 8px' }}>
                        To: {a.target === 'all' ? 'All Users' : a.target}
                      </span>
                    </div>
                    <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.65, marginBottom: 6 }}>{a.body}</p>
                    <div style={{ fontSize: 11, color: C.faint }}>{new Date(a.createdAt).toLocaleDateString()}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 7, flexShrink: 0 }}>
                    <button
                      className={a.status === 'published' ? 'btn-icon' : 'btn-success'}
                      style={a.status !== 'published' ? { padding: '7px 12px', fontSize: 12 } : {}}
                      onClick={() => togglePublish(a)}
                    >
                      {a.status === 'published' ? <RiDraftLine size={14} /> : <><RiSendPlane2Line size={13} />Publish</>}
                    </button>
                    <button className="btn-icon" onClick={() => openEdit(a)}><RiEditLine size={14} /></button>
                    <button className="btn-icon danger" onClick={() => setDelId(a._id)}><RiDeleteBinLine size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      }

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 900 }}>{editId ? 'Edit' : 'New Announcement'}</h2>
              <button className="btn-icon" onClick={() => setModal(false)}><RiCloseLine size={16} /></button>
            </div>
            <FormGroup label="Title *"><input className="ks-input" placeholder="e.g. N4 Content Now Live!" value={form.title} onChange={e => f('title', e.target.value)} /></FormGroup>
            <FormGroup label="Message *"><textarea className="ks-textarea" placeholder="Write your message here…" value={form.body} onChange={e => f('body', e.target.value)} style={{ minHeight: 100 }} /></FormGroup>
            <FormGroup label="Target Audience">
              <select className="ks-select" value={form.target} onChange={e => f('target', e.target.value)}>
                <option value="all">All Users</option>
                <option value="N5">N5 Learners</option>
                <option value="N4">N4 Learners</option>
                <option value="premium">Premium Users</option>
              </select>
            </FormGroup>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setModal(false)}>Cancel</button>
              <button className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => save(false)} disabled={saving}><RiDraftLine />Draft</button>
              <button className="btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={() => save(true)} disabled={saving}>
                {saving ? <div className="spinner" /> : <><RiSendPlane2Line />Publish</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {delId && <ConfirmModal message="This announcement will be permanently deleted." onConfirm={handleDelete} onCancel={() => setDelId(null)} />}
      <Toast message={toast} />
    </div>
  );
}
